import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, ShieldAlert, Phone, MapPin, BellRing, ChevronRight, Landmark,
  PawPrint, Search, HelpCircle, Heart, ShoppingBag,
} from 'lucide-react';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import NotificationBell from '../components/NotificationBell';
import TownFollowPicker from '../components/TownFollowPicker';
import EmergencyBanner from '../components/EmergencyBanner';
import RemovedPostNotice from '../components/safetyhub/RemovedPostNotice';
import CommunityReportCard from '../components/safetyhub/CommunityReportCard';
import ShareInfoDialog from '../components/safetyhub/ShareInfoDialog';
import { useHubT, HUB_CATEGORY_EMOJI, HUB_CATEGORY_LABEL_KEY, HUB_CATEGORY_STRIP } from '../components/safetyhub/hubStrings';
import useUnreadCount from '../hooks/useUnreadCount';
import useAuthorityProfile from '../hooks/useAuthorityProfile';
import { useAuth, API } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';

// Community Safety Hub — unified redesign (Jul 2026).
// One clean page: title · Following strip · Share button · Latest Information
// feed · Emergency Numbers row. All 4 old tabs removed; official posts carry
// an 🏛️ Official Authority badge instead of living in a separate section.

const timeAgo = (iso, lang) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return lang === 'es' ? 'ahora' : 'just now';
  if (mins < 60) return lang === 'es' ? `hace ${mins} min` : `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return lang === 'es' ? `hace ${hours} h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return lang === 'es' ? `hace ${days} d` : `${days}d ago`;
};

const SafetyBulletin = () => {
  const { t, i18n } = useTranslation();
  const hubT = useHubT();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const authority = useAuthorityProfile();
  const unreadCount = useUnreadCount();
  const { preferences } = useNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePreset, setSharePreset] = useState('');
  const [expandedId, setExpandedId] = useState('');

  const followedTowns = preferences?.areaSubscriptions || [];
  const hasFollowedTown = followedTowns.length > 0;
  const isAuthority = user?.role === 'admin' || authority.isOfficial;

  // Feb 2026 (v2) — inline post-picker options. Rendered directly on the Hub
  // page so users don't need to open a separate chooser dialog. Each option
  // either navigates to /lost-found/post with the correct itemType, or opens
  // the ShareInfoDialog pre-set to the right category.
  const postOptions = [
    isAuthority && {
      key: 'official',
      title: hubT('postOfficialTitle'),
      desc: hubT('postOfficialDesc'),
      icon: Landmark,
      accentClass: 'bg-blue-100 text-blue-700 ring-blue-200',
      share: { defaultCategory: 'community_info' },
    },
    {
      key: 'community_alert',
      title: hubT('postAlertTitle'),
      desc: hubT('postAlertDesc'),
      icon: ShieldAlert,
      accentClass: 'bg-red-100 text-red-700 ring-red-200',
      share: { defaultCategory: 'safety_hazard' },
    },
    {
      key: 'lost',
      title: hubT('postLostTitle'),
      desc: hubT('postLostDesc'),
      icon: PawPrint,
      accentClass: 'bg-blue-100 text-blue-700 ring-blue-200',
      route: '/lost-found/post?itemType=lost',
    },
    {
      key: 'found',
      title: hubT('postFoundTitle'),
      desc: hubT('postFoundDesc'),
      icon: Search,
      accentClass: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
      route: '/lost-found/post?itemType=found',
    },
    {
      key: 'help_needed',
      title: hubT('postHelpNeededTitle'),
      desc: hubT('postHelpNeededDesc'),
      icon: HelpCircle,
      accentClass: 'bg-amber-100 text-amber-700 ring-amber-200',
      share: { defaultCategory: 'help_needed' },
    },
    {
      key: 'help_offered',
      title: hubT('postHelpOfferedTitle'),
      desc: hubT('postHelpOfferedDesc'),
      icon: Heart,
      accentClass: 'bg-green-100 text-green-700 ring-green-200',
      share: { defaultCategory: 'help_offered' },
    },
    {
      // Feb 2026 v4 — Marketplace joins the picker as the 6th card so the
      // 2-column grid fills its previously empty bottom-right cell and all
      // community actions live together above "Latest Local Information".
      key: 'marketplace',
      title: hubT('marketplaceRow'),
      desc: hubT('marketplaceRowSub'),
      icon: ShoppingBag,
      accentClass: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
      route: '/marketplace',
      allowAnon: true,
    },
  ].filter(Boolean);

  const choosePostOption = (opt) => {
    // Marketplace is a browse destination, not a create action — anonymous
    // users can open it without being pushed through sign-in.
    if (!user && !opt.allowAnon) { goSignIn(); return; }
    if (opt.route) navigate(opt.route);
    else if (opt.share) {
      setSharePreset(opt.share.defaultCategory || '');
      setShareOpen(true);
    }
  };

  const goSignIn = () => navigate('/signin', {
    state: { from: `${location.pathname}${location.search || ''}` },
  });

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (followedTowns.length > 0) params.towns = followedTowns.join(',');
      const r = await axios.get(`${API}/safety-hub/feed`, {
        params,
        headers: user?.token ? { Authorization: `Bearer ${user.token}` } : {},
      });
      setItems(r.data.items || []);
    } catch { /* non-fatal */ } finally {
      setLoading(false);
    }
  }, [followedTowns.join(','), user?.token]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const openItem = (item) => {
    if (item.kind === 'group') navigate(`/incidents/${item.id}`);
    else if (item.kind === 'bulletin') navigate(`/safety/${item.id}`);
    else setExpandedId(prev => (prev === item.id ? '' : item.id));
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="safety-bulletin-page">
      <header className="bg-gradient-to-r from-slate-800 to-slate-700 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/20" data-testid="safety-back-btn">
              <ArrowLeft className="h-5 w-5 rtl-flip" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2" data-testid="hub-title">
                <Heart className="h-5 w-5" /> {hubT('hubName')}
              </h1>
              <p className="text-xs text-slate-200">{hubT('hubTagline')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell variant="on-purple" />
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5">
        <EmergencyBanner compact />
        <RemovedPostNotice />

        {unreadCount > 0 && (
          <button
            onClick={() => navigate('/notifications')}
            className="w-full flex items-center gap-2 bg-white border border-red-200 rounded-xl px-4 py-2.5 mb-3 shadow-sm hover:bg-red-50 transition-colors"
            data-testid="hub-unread-chip"
          >
            <BellRing className="h-4 w-4 text-red-600" />
            <span className="text-sm font-semibold text-gray-800">{hubT('unreadNotifications')}</span>
            <span className="ml-auto min-w-[22px] h-[22px] px-1.5 bg-red-500 rounded-full text-[11px] flex items-center justify-center text-white font-bold" data-testid="hub-unread-count">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </button>
        )}

        {/* 📍 Following strip */}
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 mb-4 shadow-sm flex items-center gap-3 flex-wrap" data-testid="hub-following-strip">
          <span className="flex items-center gap-1.5 text-sm font-bold text-gray-900">
            <MapPin className="h-4 w-4 text-slate-600" /> {hubT('following')}
          </span>
          <span className="text-sm text-gray-700 flex-1 min-w-0 truncate" data-testid="hub-following-towns">
            {hasFollowedTown
              ? followedTowns.slice(0, 3).join(' • ') + (followedTowns.length > 3 ? ` • +${followedTowns.length - 3} ${hubT('moreTowns')}` : '')
              : hubT('followNoTowns')}
          </span>
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="hub-manage-towns-btn">
            {hubT('manageTowns')}
          </Button>
        </div>

        {/* Feb 2026 (v2) — inline "What would you like to post?" grid.
            Replaces the previous "+ Create Community Post" button + chooser
            dialog and the duplicate Lost & Found card. Users see the 5 (or 6
            for authorities) options immediately upon entering the Hub. */}
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 mb-5 shadow-sm" data-testid="hub-post-picker">
          <h2 className="text-sm font-bold text-gray-900" data-testid="hub-post-picker-title">
            {hubT('postPickerTitle')}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">{hubT('postPickerSubtitle')}</p>
          {/* Feb 2026 v5 — fixed 2×3 grid on all screen sizes. Users asked
              for a consistent balanced layout that never collapses to a
              single column, even on narrow mobiles. Six cards always. */}
          <div className="grid grid-cols-2 gap-2" data-testid="hub-post-picker-grid">
            {postOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => choosePostOption(opt)}
                className="flex flex-col items-start gap-2 p-3 rounded-lg border border-gray-100 hover:border-purple-300 hover:bg-purple-50 transition-colors text-left h-full"
                data-testid={`hub-post-option-${opt.key}`}
              >
                <span className={`p-2 rounded-lg ring-1 shrink-0 ${opt.accentClass}`} aria-hidden="true">
                  <opt.icon className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0 w-full">
                  <span className="block text-sm font-semibold text-gray-900 leading-snug">{opt.title}</span>
                  <span className="block text-[11px] text-gray-500 mt-0.5 leading-snug">{opt.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Latest Information feed */}
        <h2 className="text-base font-bold text-gray-900 mb-3" data-testid="hub-latest-heading">{hubT('latestInfo')}</h2>
        {loading ? (
          <div className="text-center text-gray-400 py-12">…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-200 shadow-sm" data-testid="hub-feed-empty">
            <div className="text-4xl mb-3" aria-hidden="true">👍</div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{hubT('noInfoTitle')}</h3>
            <p className="text-sm text-gray-600">{hubT('noInfoBody')}</p>
            <p className="text-sm text-gray-500 mt-2">{hubT('noInfoCta')}</p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="hub-feed">
            {items.map((item) => {
              const emoji = HUB_CATEGORY_EMOJI[item.category] || '📢';
              const stripClass = HUB_CATEGORY_STRIP[item.category] || 'bg-gray-400';
              const catLabel = hubT(HUB_CATEGORY_LABEL_KEY[item.category] || 'catInfo');
              const townLabel = item.isBroadcast ? hubT('allTowns') : (item.town || '');
              const isGroup = item.kind === 'group';
              const expanded = item.kind === 'report' && expandedId === item.id;
              const hasUpdates = (item.updateCount || 0) > 0;
              const timeStr = timeAgo(item.lastActivityAt, i18n.language);
              const activityLine = hasUpdates
                ? `${item.updateCount} ${item.updateCount === 1 ? hubT('update') : hubT('updates')} • ${hubT('updated')} ${timeStr}`
                : `${hubT('posted')} ${timeStr}`;
              // v6.3.1 — an UPCOMING pill on the collapsed feed row so
              // scrollers can spot advance warnings without opening the card.
              const rowEventStart = item.report?.eventStartAt || null;
              const rowIsUpcoming = item.kind === 'report' && !!rowEventStart && rowEventStart > new Date().toISOString();
              return (
                <div key={`${item.kind}-${item.id}`}>
                  <button
                    onClick={() => openItem(item)}
                    className={`w-full text-left bg-white rounded-xl border shadow-sm hover:shadow transition-shadow overflow-hidden ${
                      item.priority === 1 ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200'
                    }`}
                    data-testid={`hub-item-${item.id}`}
                  >
                    <div className="flex items-stretch">
                      <div className={`w-1 shrink-0 ${stripClass}`} aria-hidden="true" data-testid={`hub-item-strip-${item.id}`} />
                      <div className="flex-1 min-w-0 p-4 flex items-start gap-3">
                        <span className="text-xl leading-none mt-0.5" aria-hidden="true">{emoji}</span>
                        <div className="flex-1 min-w-0">
                          {townLabel && (
                            <p className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-1" data-testid={`hub-item-town-${item.id}`}>
                              <MapPin className="h-3 w-3 text-gray-500" /> {townLabel}
                            </p>
                          )}
                          <p className="text-sm font-semibold text-gray-900 leading-snug">{item.title}</p>
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                            {item.isOfficial && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-800 bg-blue-50 ring-1 ring-blue-200 px-2 py-0.5 rounded-full" data-testid={`hub-official-badge-${item.id}`}>
                                <Landmark className="h-3 w-3" /> {hubT('officialAuthority')}
                              </span>
                            )}
                            {rowIsUpcoming && (
                              <span
                                className="inline-flex items-center text-[11px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 ring-1 ring-amber-300 px-2 py-0.5 rounded-full"
                                data-testid={`hub-item-upcoming-${item.id}`}
                              >
                                {hubT('upcoming')}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">{activityLine}</span>
                            <span className="text-xs text-gray-400">• {catLabel}</span>
                          </div>
                        </div>
                        <span className="flex items-center gap-0.5 text-xs font-semibold text-slate-600 shrink-0 mt-1">
                          {isGroup ? hubT('viewUpdates') : hubT('open')} <ChevronRight className="h-3.5 w-3.5 rtl-flip" />
                        </span>
                      </div>
                    </div>
                  </button>
                  {expanded && item.report && (
                    <div className="mt-1.5 ml-3 border-l-2 border-slate-200 pl-3" data-testid={`hub-item-expanded-${item.id}`}>
                      <CommunityReportCard report={item.report} onChanged={loadFeed} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ☎️ Emergency Numbers — slim row at the bottom */}
        <button
          onClick={() => navigate('/emergency')}
          className="w-full flex items-center gap-3 bg-white border border-red-100 rounded-xl px-4 py-3 mt-6 shadow-sm hover:bg-red-50 transition-colors text-left"
          data-testid="hub-emergency-numbers-link"
        >
          <Phone className="h-4 w-4 text-red-500 shrink-0" aria-hidden="true" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-gray-900">{hubT('emergencyRow')}</span>
            <span className="block text-xs text-gray-500">{hubT('emergencyRowSub')}</span>
          </span>
          <span className="text-xs font-semibold text-slate-600 flex items-center gap-0.5">
            {hubT('view')} <ChevronRight className="h-3.5 w-3.5 rtl-flip" />
          </span>
        </button>

        <p className="text-[11px] text-gray-400 text-center mt-3">
          {i18n.language === 'es' ? 'En caso de emergencia llama siempre al 112.' : 'In an emergency always call 112.'}
        </p>
      </main>

      <ShareInfoDialog
        open={shareOpen}
        onClose={() => { setShareOpen(false); setSharePreset(''); }}
        onShared={loadFeed}
        isAuthority={isAuthority}
        defaultCategory={sharePreset}
      />
      <TownFollowPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
      <BottomNavigation />
    </div>
  );
};

export default SafetyBulletin;
